import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import PhoneInput from 'components/FormFields/PhoneInput';
import ThemedSelect from 'components/ThemedSelect';
import { PlainButton } from 'components/v3';
import { OTHER_VALUE } from '../../Preview/inputs/ChoiceInputs';
import { phoneCountry } from '../../Preview/inputs/PersonInputs';
import { OTHER_LABEL } from '../../shared/fieldTypes';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';
import { hasFiles, isAgreement } from './entryModel';
import EntryValue, {
  MiniGrid,
  MiniRow,
  Star,
  gridTemplate,
} from './EntryValue';

// Edit controls for one answer on the entry card (design entry_detail.jsx
// Editor). Values keep the stored answer shapes (name / address objects,
// { cc, number } phones, "YYYY-MM-DD" dates, table "row-col" maps…).
// Choices use custom controls — a themed react-select for dropdowns and
// radio buttons, pills for checkboxes and matrix rows — never a native
// <select>. Uploaded files can't be swapped here, and neither can the
// W-9's tax details (tin, signature, tax_class: w9-plan T16).

export const EdInput = styled.input`
  display: block;
  width: 100%;
  min-width: 0;
  height: ${(p) => (p.$sm ? '34px' : '38px')};
  box-sizing: border-box;
  padding: 0 ${(p) => (p.$sm ? '10px' : '13px')};
  border-radius: 6px;
  border: 1px solid
    ${(p) => (p.$invalid ? sx(p.theme).error : sx(p.theme).line2)};
  background: ${(p) => sx(p.theme).field};
  color: ${(p) => sx(p.theme).text};
  font-size: ${(p) => (p.$sm ? '13px' : '14px')};
  font-weight: 600;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;

  &::placeholder {
    color: ${(p) => sx(p.theme).disabled};
    font-weight: 500;
  }

  &:focus {
    border-color: ${ORANGE};
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.2);
  }
`;

const EdTextarea = styled(EdInput).attrs({ as: 'textarea' })`
  height: auto;
  min-height: 84px;
  padding: 9px 13px;
  line-height: 1.5;
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: ${(p) => (p.$wrap ? 'wrap' : 'nowrap')};
  gap: 10px;

  & > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Segmented = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).inset};
`;

const SegButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => {
    const x = sx(p.theme);
    if (!p.$on) return x.label;
    return p.$ok ? x.ok : x.text;
  }};
  background: ${(p) => {
    const x = sx(p.theme);
    if (!p.$on) return 'transparent';
    return p.$ok ? x.okBg : x.fill;
  }};
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Pill = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  box-sizing: border-box;
  padding: 4px 11px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$on ? sx(p.theme).tintB : sx(p.theme).line2)};
  background: ${(p) => (p.$on ? sx(p.theme).tint : sx(p.theme).inset)};
  color: ${(p) => (p.$on ? sx(p.theme).otext : sx(p.theme).text2)};
  font-size: 13px;
  font-weight: 600;
  text-align: left;
`;

const StarButton = styled(PlainButton)`
  display: flex;
  padding: 2px;
  color: ${(p) => sx(p.theme).disabled};
`;

const RemoveButton = styled(PlainButton)`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => sx(p.theme).label};

  &:hover {
    color: ${(p) => sx(p.theme).error};
    background: ${(p) => sx(p.theme).errorBg};
  }
`;

const AddButton = styled(PlainButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 42px;
  border-top: 1px dashed ${(p) => sx(p.theme).line2};
  color: ${(p) => sx(p.theme).otext};
  font-size: 14px;
  font-weight: 700;

  &:hover {
    background: ${(p) => sx(p.theme).tint};
  }
`;

const ReadOnly = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  font-size: 15px;
  font-weight: 700;
`;

const Hint = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  color: ${(p) => sx(p.theme).label};
`;

const MatrixRow = styled.div`
  display: grid;
  gap: 6px;
`;

const MatrixLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => sx(p.theme).text2};
`;

export const TAX_DETAILS_HINT =
  'Tax details stay exactly as the driver signed them';

const objectOf = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const textOf = (value) => (typeof value === 'string' ? value : '');
const optionsOf = (field) =>
  ((field.config && field.config.options) || []).filter(
    (o, i, all) => typeof o === 'string' && o && all.indexOf(o) === i
  );
const columnsOf = (field) => (field.config && field.config.columns) || [];

// ---- date: typed as MM/DD/YYYY, stored as YYYY-MM-DD ----------------------

export const isoToDisplay = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(textOf(value));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : textOf(value);
};

// Digits typed so far → "MM/DD/YYYY" with the slashes added; a complete
// date becomes "YYYY-MM-DD", anything else is kept as typed (the server
// says what's wrong with it).
export const displayToValue = (typed) => {
  const digits = String(typed || '')
    .replace(/\D/g, '')
    .slice(0, 8);
  if (digits.length === 8) {
    return `${digits.slice(4)}-${digits.slice(0, 2)}-${digits.slice(2, 4)}`;
  }
  if (digits.length > 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
};

// ---- choice controls ------------------------------------------------------

const selectStyles = (theme) => {
  const x = sx(theme);
  return {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderRadius: 6,
      backgroundColor: x.field,
      borderColor: state.isFocused ? ORANGE : x.line2,
      boxShadow: state.isFocused ? '0 0 0 3px rgba(250, 70, 22, 0.2)' : 'none',
      cursor: 'pointer',
      '&:hover': { borderColor: state.isFocused ? ORANGE : x.ctl },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 12px' }),
    singleValue: (base) => ({
      ...base,
      color: x.text,
      fontSize: 14,
      fontWeight: 600,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };
};

// With config.allow_other the list ends in "Other…", which opens a text box;
// the typed text is the answer. A stored answer that isn't an option shows as
// Other with its text (same as the preview's ChoiceInputs).
const ChoiceSelect = ({ field, value, onChange, inputId }) => {
  const theme = useTheme();
  const listed = optionsOf(field);
  const allowOther = !!(field.config && field.config.allow_other === true);
  const current = textOf(value);
  const [otherPicked, setOtherPicked] = useState(false);
  const other =
    allowOther &&
    (otherPicked || (!!current && listed.indexOf(current) === -1));
  const otherOption = { value: OTHER_VALUE, label: `${OTHER_LABEL}…` };
  const options = listed.map((o) => ({ value: o, label: o }));
  if (allowOther) options.push(otherOption);
  let selected = null;
  if (other) selected = otherOption;
  else if (current) selected = { value: current, label: current };
  const pick = (opt) => {
    if (opt && opt.value === OTHER_VALUE) {
      setOtherPicked(true);
      onChange('');
      return;
    }
    setOtherPicked(false);
    onChange(opt ? opt.value : null);
  };
  return (
    <div>
      <ThemedSelect
        inputId={inputId}
        aria-label={field.label || 'Answer'}
        options={options}
        value={selected}
        onChange={pick}
        isClearable
        isSearchable={options.length > 7}
        placeholder="—"
        menuPortalTarget={
          typeof document === 'undefined' ? null : document.body
        }
        customStyles={selectStyles(theme)}
      />
      {other ? (
        <div style={{ marginTop: 8 }}>
          <EdInput
            aria-label={`${field.label || 'Answer'}: ${OTHER_LABEL}`}
            placeholder="Type the answer"
            value={current}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      ) : null}
    </div>
  );
};

ChoiceSelect.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  inputId: PropTypes.string,
};
ChoiceSelect.defaultProps = { value: undefined, inputId: undefined };

const AgreementEditor = ({ field, value, onChange }) => (
  <Segmented role="group" aria-label={field.label || 'Agreement'}>
    <SegButton
      $on={value === true}
      $ok
      aria-pressed={value === true}
      onClick={() => onChange(true)}
    >
      <Dot />
      Agreed
    </SegButton>
    <SegButton
      $on={value !== true}
      aria-pressed={value !== true}
      onClick={() => onChange(false)}
    >
      Not agreed
    </SegButton>
  </Segmented>
);

AgreementEditor.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
};
AgreementEditor.defaultProps = { value: undefined };

const CheckboxEditor = ({ field, value, onChange }) => {
  const picked = Array.isArray(value) ? value : [];
  const options = optionsOf(field);
  const toggle = (option) =>
    onChange(
      picked.includes(option)
        ? picked.filter((o) => o !== option)
        : options.filter((o) => o === option || picked.includes(o))
    );
  return (
    <Pills role="group" aria-label={field.label || 'Choices'}>
      {options.map((option) => (
        <Pill
          key={option}
          $on={picked.includes(option)}
          aria-pressed={picked.includes(option)}
          onClick={() => toggle(option)}
        >
          {option}
        </Pill>
      ))}
    </Pills>
  );
};

CheckboxEditor.propTypes = AgreementEditor.propTypes;
CheckboxEditor.defaultProps = { value: undefined };

const RatingEditor = ({ field, value, onChange }) => {
  const n = Number(value) || 0;
  const max = (field.config && field.config.max) || 5;
  return (
    <div
      role="group"
      aria-label={field.label || 'Rating'}
      style={{ display: 'flex', gap: 3 }}
    >
      {Array.from({ length: max }, (_, i) => (
        <StarButton
          key={i}
          aria-label={`${i + 1} of ${max}`}
          aria-pressed={i < n}
          onClick={() => onChange(i + 1 === n ? null : i + 1)}
        >
          <Star $on={i < n}>
            <Glyph name="star" size={19} />
          </Star>
        </StarButton>
      ))}
    </div>
  );
};

RatingEditor.propTypes = AgreementEditor.propTypes;
RatingEditor.defaultProps = { value: undefined };

const MatrixEditor = ({ field, value, onChange }) => {
  const v = objectOf(value);
  const rows = (field.config && field.config.rows) || [];
  const cols = (field.config && field.config.cols) || [];
  return (
    <Stack>
      {rows.map((row) => (
        <MatrixRow key={row} role="group" aria-label={row}>
          <MatrixLabel>{row}</MatrixLabel>
          <Pills>
            {cols.map((col) => (
              <Pill
                key={col}
                $on={v[row] === col}
                aria-pressed={v[row] === col}
                onClick={() =>
                  onChange({ ...v, [row]: v[row] === col ? '' : col })
                }
              >
                {col}
              </Pill>
            ))}
          </Pills>
        </MatrixRow>
      ))}
    </Stack>
  );
};

MatrixEditor.propTypes = AgreementEditor.propTypes;
MatrixEditor.defaultProps = { value: undefined };

// ---- grids ----------------------------------------------------------------

const TableEditor = ({ field, value, onChange }) => {
  const v = objectOf(value);
  const columns = columnsOf(field);
  const count = Number(field.config && field.config.rows) || 0;
  const tpl = gridTemplate(columns.length);
  return (
    <MiniGrid>
      <MiniRow $head $first style={tpl}>
        {columns.map((c, i) => (
          <div key={i}>{c}</div>
        ))}
      </MiniRow>
      {Array.from({ length: count }, (_, r) => (
        <MiniRow key={r} style={tpl}>
          {columns.map((c, ci) => (
            <EdInput
              key={ci}
              $sm
              aria-label={`${c}, row ${r + 1}`}
              placeholder={c}
              value={textOf(v[`${r}-${ci}`])}
              onChange={(e) =>
                onChange({ ...v, [`${r}-${ci}`]: e.target.value })
              }
            />
          ))}
        </MiniRow>
      ))}
    </MiniGrid>
  );
};

TableEditor.propTypes = AgreementEditor.propTypes;
TableEditor.defaultProps = { value: undefined };

const RepeaterEditor = ({ field, value, onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const columns = columnsOf(field);
  const max = Number(field.config && field.config.max) || 0;
  const tpl = {
    gridTemplateColumns: `repeat(${Math.max(
      1,
      columns.length
    )}, minmax(0, 1fr)) 36px`,
  };
  const setCell = (r, c, text) =>
    onChange(
      rows.map((row, i) => (i === r ? { ...objectOf(row), [c]: text } : row))
    );
  return (
    <MiniGrid>
      <MiniRow $head $first style={tpl}>
        {columns.map((c, i) => (
          <div key={i}>{c}</div>
        ))}
        <div />
      </MiniRow>
      {rows.map((row, r) => (
        <MiniRow key={r} style={tpl}>
          {columns.map((c, ci) => (
            <EdInput
              key={ci}
              $sm
              aria-label={`${c}, entry ${r + 1}`}
              placeholder={c}
              value={textOf(objectOf(row)[ci])}
              onChange={(e) => setCell(r, ci, e.target.value)}
            />
          ))}
          <RemoveButton
            aria-label={`Remove entry ${r + 1}`}
            onClick={() => onChange(rows.filter((_, i) => i !== r))}
          >
            <Glyph name="x" size={15} />
          </RemoveButton>
        </MiniRow>
      ))}
      {!max || rows.length < max ? (
        <AddButton onClick={() => onChange(rows.concat({}))}>
          <Glyph name="plus" size={14} />
          {(field.config && field.config.add_label) || 'Add row'}
        </AddButton>
      ) : null}
    </MiniGrid>
  );
};

RepeaterEditor.propTypes = AgreementEditor.propTypes;
RepeaterEditor.defaultProps = { value: undefined };

// ---- people ---------------------------------------------------------------

const PhoneEditor = ({ field, value, onChange, inputId }) => {
  const v = objectOf(value);
  const cc = textOf(v.cc) || '+1';
  const [pickedIso, setPickedIso] = useState(null);
  return (
    <PhoneInput
      label=""
      id={inputId}
      aria-label={field.label || 'Phone'}
      inputMode="tel"
      autoComplete="off"
      countryCode={phoneCountry(cc, pickedIso)}
      value={textOf(v.number)}
      onChange={(number) => onChange({ cc, number })}
      onCountryChange={(code, iso) => {
        setPickedIso(iso);
        onChange({ cc: code, number: textOf(v.number) });
      }}
    />
  );
};

PhoneEditor.propTypes = ChoiceSelect.propTypes;
PhoneEditor.defaultProps = { value: undefined, inputId: undefined };

const parts = (value, onChange) => {
  const v = objectOf(value);
  return [v, (key) => (e) => onChange({ ...v, [key]: e.target.value })];
};

// ---- the editor -----------------------------------------------------------

const EntryEditor = ({ field, value, onChange, inputId, invalid }) => {
  const common = { $invalid: invalid, 'aria-invalid': invalid || undefined };
  if (isAgreement(field)) {
    return <AgreementEditor field={field} value={value} onChange={onChange} />;
  }
  switch (field.type) {
    case 'name': {
      const [v, set] = parts(value, onChange);
      const middle = !!(field.config && field.config.middle);
      return (
        <Row $wrap>
          <EdInput
            {...common}
            id={inputId}
            aria-label="First name"
            placeholder="First"
            value={textOf(v.first)}
            onChange={set('first')}
          />
          {middle ? (
            <EdInput
              {...common}
              aria-label="Middle name"
              placeholder="Middle"
              value={textOf(v.middle)}
              onChange={set('middle')}
            />
          ) : null}
          <EdInput
            {...common}
            aria-label="Last name"
            placeholder="Last"
            value={textOf(v.last)}
            onChange={set('last')}
          />
        </Row>
      );
    }
    case 'address': {
      const [v, set] = parts(value, onChange);
      return (
        <Stack>
          <EdInput
            {...common}
            id={inputId}
            aria-label="Street address"
            placeholder="Street address"
            value={textOf(v.street)}
            onChange={set('street')}
          />
          <EdInput
            {...common}
            aria-label="Apartment, suite"
            placeholder="Apt, suite (optional)"
            value={textOf(v.street2)}
            onChange={set('street2')}
          />
          <Row $wrap>
            <EdInput
              {...common}
              aria-label="City"
              placeholder="City"
              value={textOf(v.city)}
              onChange={set('city')}
              style={{ flexBasis: 160 }}
            />
            <EdInput
              {...common}
              aria-label="State"
              placeholder="State"
              value={textOf(v.state)}
              onChange={set('state')}
              style={{ flex: '0 0 96px' }}
            />
            <EdInput
              {...common}
              aria-label="ZIP"
              placeholder="ZIP"
              inputMode="numeric"
              value={textOf(v.zip)}
              onChange={set('zip')}
              style={{ flex: '0 0 120px' }}
            />
          </Row>
        </Stack>
      );
    }
    case 'phone':
      return (
        <PhoneEditor
          field={field}
          value={value}
          onChange={onChange}
          inputId={inputId}
        />
      );
    case 'dropdown':
    case 'radio':
      return (
        <ChoiceSelect
          field={field}
          value={value}
          onChange={onChange}
          inputId={inputId}
        />
      );
    case 'checkbox':
      return <CheckboxEditor field={field} value={value} onChange={onChange} />;
    case 'rating':
      return <RatingEditor field={field} value={value} onChange={onChange} />;
    case 'matrix':
      return <MatrixEditor field={field} value={value} onChange={onChange} />;
    case 'table':
      return <TableEditor field={field} value={value} onChange={onChange} />;
    case 'repeater':
      return <RepeaterEditor field={field} value={value} onChange={onChange} />;
    case 'file_upload':
      return (
        <ReadOnly>
          <EntryValue
            field={field}
            value={hasFiles(field, value) ? value : null}
          />
          <Hint>Uploads can’t be swapped here</Hint>
        </ReadOnly>
      );
    case 'tin':
    case 'signature':
    case 'tax_class':
      return (
        <ReadOnly>
          <EntryValue field={field} value={value} />
          <Hint>{TAX_DETAILS_HINT}</Hint>
        </ReadOnly>
      );
    case 'long_answer':
      return (
        <EdTextarea
          {...common}
          id={inputId}
          rows={3}
          placeholder={field.placeholder || ''}
          value={textOf(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'datetime':
      return (
        <EdInput
          {...common}
          id={inputId}
          inputMode="numeric"
          placeholder="MM/DD/YYYY"
          value={isoToDisplay(value)}
          onChange={(e) => onChange(displayToValue(e.target.value))}
        />
      );
    default:
      return (
        <EdInput
          {...common}
          id={inputId}
          type={field.type === 'email' ? 'email' : 'text'}
          inputMode={field.type === 'number' ? 'decimal' : undefined}
          placeholder={field.placeholder || ''}
          value={textOf(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

EntryEditor.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    config: PropTypes.object,
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  inputId: PropTypes.string,
  invalid: PropTypes.bool,
};

EntryEditor.defaultProps = {
  value: undefined,
  inputId: undefined,
  invalid: false,
};

export default EntryEditor;
